import { useCallback, useReducer } from "react";

import { loadOlderMessages, sendKeystroke, sendMarkRead } from "./actions";
import { MESSAGE_PAGE_SIZE } from "../constants";
import {
  flattenRawMessage,
  prependChannelMessages,
} from "../entities/messages";
import { messagesByChannelId, usersTypingByChannelId } from "../state";

const initialState = {
  // the first page is fetched as part of loadInitialState
  page: 1,
  hasOlder: true,
  olderLoading: false,
  error: null,
};

function reducer(state, action) {
  const { type, ...rest } = action;
  switch (type) {
    case "SET_STATE":
      return { ...state, ...rest };
    default:
      // eslint-disable-next-line no-console
      console.error(
        `useChannelMessages reducer does not support type: '${action.type}'`
      );
      return state;
  }
}

export default function useChannelMessages({ channelId }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { hasOlder, olderLoading, error } = state;

  const messages = messagesByChannelId.use((obj) => obj[channelId]) || [];
  const typingUsers = usersTypingByChannelId.use((obj) => obj[channelId]) || [];
  const oldestMessage = messages[0];
  const oldestMessageId = oldestMessage && oldestMessage.messageId;

  const onKeyStroke = useCallback(() => sendKeystroke(channelId), [channelId]);

  const loadOlder = useCallback(async () => {
    dispatch({ type: "SET_STATE", olderLoading: true });
    const newMessages = await loadOlderMessages(
      channelId,
      oldestMessageId
    ).catch((err) => dispatch({ type: "SET_STATE", error: err }));
    if (newMessages.length) {
      const flattenedMessages = newMessages.map(flattenRawMessage);
      prependChannelMessages(channelId, flattenedMessages);
    }
    const newHasOlder = newMessages.length >= MESSAGE_PAGE_SIZE;
    dispatch({ type: "SET_STATE", hasOlder: newHasOlder, olderLoading: false });
  }, [channelId, oldestMessageId]);

  const markRead = useCallback(() => sendMarkRead(channelId), [channelId]);

  return {
    hasOlder,
    olderLoading,
    error,
    messages,
    typingUsers,
    onKeyStroke,
    loadOlder,
    markRead,
  };
}
